import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { AppLink, RichText } from "@/components";
import {
  BRAND_LABEL,
  FOOTER_ADDRESS,
  FOOTER_CLAIM,
  FOOTER_COLUMNS,
  FOOTER_COPYRIGHT_SUFFIX,
  FOOTER_TAGLINE,
  HOME_PATH,
  media,
  resolveAnchor,
} from "@/content";

export const Footer = () => {
  const isHome = useLocation().pathname === HOME_PATH;

  return (
    <Box
      as="footer"
      className="print-flat"
      bg="surface.dark"
      color="ink.light"
      px="pad"
      pt="clamp(56px, 7vw, 100px)"
      pb="clamp(24px, 3vw, 40px)"
    >
      <Grid
        maxW="maxw"
        mx="auto"
        gap="clamp(20px, 3vw, 40px)"
        pb="clamp(40px, 5vw, 72px)"
        templateColumns={{ base: "1fr", lg: "auto 1fr" }}
        alignItems={{ lg: "center" }}
      >
        <AppLink href={HOME_PATH} aria-label={BRAND_LABEL} justifySelf="start">
          <img
            src={media("variant3d-logo.png")}
            alt="VARIANT3D"
            width={447}
            height={76}
            loading="lazy"
            style={{ height: 24, width: "auto" }}
          />
        </AppLink>
        <Text
          fontSize="clamp(1.125rem, 2.2vw, 2rem)"
          fontWeight="light"
          lineHeight={1}
          textTransform="uppercase"
          color="ink.light2"
          maxW="30ch"
          justifySelf={{ base: "start", lg: "end" }}
          sx={{ textWrap: "balance" }}
        >
          {FOOTER_CLAIM}
        </Text>
      </Grid>

      <Grid
        maxW="maxw"
        mx="auto"
        gap="clamp(28px, 4vw, 48px)"
        templateColumns="repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
        py="clamp(32px, 4vw, 48px)"
        borderTop="1px solid"
        borderColor="line.glassBorder"
      >
        {FOOTER_COLUMNS.map((column) => (
          <Box key={column.id}>
            <Text as="h2" variant="eyebrow" color="warm.500" mb="18px">
              {column.heading}
            </Text>
            <FooterLinks column={column} isHome={isHome} />
          </Box>
        ))}
      </Grid>

      <Flex
        maxW="maxw"
        mx="auto"
        wrap="wrap"
        justify="space-between"
        gap="12px"
        pt="clamp(20px, 2.4vw, 28px)"
        borderTop="1px solid"
        borderColor="line.glassBorder"
        fontSize="eyebrow"
        letterSpacing="0.06em"
        textTransform="uppercase"
        color="warm.500"
      >
        <Text>
          © {new Date().getFullYear()} {FOOTER_COPYRIGHT_SUFFIX}
        </Text>
        <Text>
          <RichText nodes={FOOTER_TAGLINE} />
        </Text>
      </Flex>
    </Box>
  );
};

const FooterLinks = ({ column, isHome }: { column: (typeof FOOTER_COLUMNS)[number]; isHome: boolean }) => (
  <Box as="ul" role="list" display="grid" gap="12px">
    {column.links.map((entry) => (
      <Box as="li" key={entry.href} fontSize="small" lineHeight={1.4} color="ink.light2">
        <AppLink
          href={resolveAnchor(entry.href, isHome)}
          external={entry.external}
          display="inline-flex"
          alignItems="center"
          gap="8px"
          transitionProperty="color"
          transitionDuration="0.2s"
          transitionTimingFunction="brand"
          _hover={{ color: "brand.500" }}
        >
          {column.id === "follow" && <SocialIcon platform={entry.label} />}
          {entry.label}
        </AppLink>
      </Box>
    ))}
    {column.id === "contact" && (
      <Box as="li" fontSize="small" lineHeight={1.4} color="ink.light2">
        {FOOTER_ADDRESS.map((line) => (
          <Box key={line}>{line}</Box>
        ))}
      </Box>
    )}
  </Box>
);

const SocialIcon = ({ platform }: { platform: string }) => {
  const commonProps = {
    "aria-hidden": true,
    focusable: false,
    viewBox: "0 0 24 24",
    width: "16",
    height: "16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    style: { flexShrink: 0 },
  };

  switch (platform) {
    case "Instagram":
      return (
        <svg {...commonProps}>
          <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.75" />
          <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.75" />
          <circle cx="17.4" cy="6.7" r="1" fill="currentColor" />
        </svg>
      );
    case "LinkedIn":
      return (
        <svg {...commonProps} fill="currentColor">
          <path d="M5.4 8.1A1.85 1.85 0 1 0 5.4 4.4a1.85 1.85 0 0 0 0 3.7ZM3.8 9.5h3.2V20H3.8V9.5Zm5.2 0h3.05v1.44h.05c.43-.8 1.47-1.66 3.02-1.66 3.23 0 3.83 2.12 3.83 4.88V20h-3.18v-5.2c0-1.24-.02-2.84-1.73-2.84-1.74 0-2 1.35-2 2.75V20H9V9.5Z" />
        </svg>
      );
    case "YouTube":
      return (
        <svg {...commonProps}>
          <rect x="2.5" y="6.5" width="19" height="11" rx="3" stroke="currentColor" strokeWidth="1.75" />
          <path d="m10 9.5 5 2.5-5 2.5v-5Z" fill="currentColor" />
        </svg>
      );
    case "Discord":
      return (
        <svg {...commonProps}>
          <path
            d="M7 6.3c3.1-1.1 6.9-1.1 10 0 1.15 1.7 1.85 3.75 2 5.9-.8 1.2-1.86 2.24-3.1 3.03l-1.05-1.38c.4-.15.79-.35 1.15-.58-2.18 1.02-5.72 1.02-7.9 0 .36.23.75.43 1.15.58L8.2 15.23a10.4 10.4 0 0 1-3.1-3.03c.15-2.15.85-4.2 2-5.9Z"
            stroke="currentColor"
            strokeWidth="1.55"
            strokeLinejoin="round"
          />
          <circle cx="9.4" cy="11.45" r="1.1" fill="currentColor" />
          <circle cx="14.6" cy="11.45" r="1.1" fill="currentColor" />
        </svg>
      );
    default:
      return null;
  }
};
